import React, { useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import './InView.css';

/**
 * Animation types available in the InView component
 */
export const ANIMATIONS = {
  FADE: 'fade-animation',
  SLIDE_UP: 'slide-up-animation',
  SLIDE_DOWN: 'slide-down-animation',
  SLIDE_LEFT: 'slide-left-animation',
  SLIDE_RIGHT: 'slide-right-animation',
  SCALE: 'scale-animation',
  NONE: 'none'
};

/**
 * InView - A versatile component for handling scroll-based interactions
 * 
 * @example
 * // Basic animation
 * <InView animation="fade">
 *   <div>Animates when in view</div>
 * </InView>
 * 
 * // Visibility tracking
 * <InView onEnter={() => console.log('Element entered view')}>
 *   <div>Tracks visibility</div>
 * </InView>
 * 
 * // Custom visibility handling
 * <InView>
 *   {({ inView, entry }) => (
 *     <div>{inView ? 'Visible' : 'Hidden'}</div>
 *   )}
 * </InView>
 */
const InView = ({
  children,
  // Animation props
  animation,
  delay = 0,
  duration = 0.8,
  cascade = false,
  cascadeDelay = 0.1,
  // Observer props
  threshold = 0.2,
  root = null,
  rootMargin = '0px',
  triggerOnce = true,
  // Callback props
  onEnter,
  onLeave,
  onChange,
  // Component props
  as: Component = 'div',
  className = '',
  ...props
}) => {
  const elementRef = useRef(null);
  const observerRef = useRef(null);
  const [isInView, setIsInView] = React.useState(false);
  const prevInView = useRef(false);

  const handleIntersection = useCallback(([entry]) => {
    const inView = entry.isIntersecting;
    setIsInView(inView);

    // Handle animation classes
    if (animation && entry.isIntersecting) {
      requestAnimationFrame(() => {
        entry.target.classList.add('in-view');
        if (cascade) {
          entry.target.classList.add('animate-cascade');
        }
        if (triggerOnce) {
          observerRef.current?.unobserve(entry.target);
        }
      });
    } else if (animation && !triggerOnce && !entry.isIntersecting) {
      requestAnimationFrame(() => {
        entry.target.classList.remove('in-view');
      });
    }

    // Handle callbacks
    if (inView !== prevInView.current) {
      if (inView && onEnter) onEnter(entry);
      if (!inView && onLeave) onLeave(entry);
      if (onChange) onChange(inView, entry);
      prevInView.current = inView;
    }
  }, [animation, cascade, triggerOnce, onEnter, onLeave, onChange]);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    // Set animation properties if needed
    if (animation) {
      element.style.setProperty('--animation-delay', `${delay}s`);
      element.style.setProperty('--animation-duration', `${duration}s`);
      element.style.setProperty('--cascade-delay', `${cascadeDelay}s`);

      if (cascade) {
        Array.from(element.children).forEach((child, index) => {
          child.style.setProperty('--animation-delay', 
            `${delay + (index * cascadeDelay)}s`);
          child.classList.add('animate-item');
        });
      }
    }

    // Create and configure observer
    observerRef.current = new IntersectionObserver(handleIntersection, {
      threshold,
      root,
      rootMargin,
    });

    observerRef.current.observe(element);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [
    animation,
    delay,
    duration,
    cascade,
    cascadeDelay,
    threshold,
    root,
    rootMargin,
    handleIntersection,
  ]);

  const classes = [
    'in-view-wrapper',
    animation && animation,
    className,
  ].filter(Boolean).join(' ');

  // Handle render prop pattern
  if (typeof children === 'function') {
    return children({ 
      ref: elementRef, 
      inView: isInView, 
      entry: observerRef.current?.takeRecords()?.[0] 
    });
  }

  return (
    <Component
      ref={elementRef}
      className={classes}
      {...props}
    >
      {children}
    </Component>
  );
};

InView.propTypes = {
  // Animation props
  animation: PropTypes.oneOf(Object.values(ANIMATIONS)),
  delay: PropTypes.number,
  duration: PropTypes.number,
  cascade: PropTypes.bool,
  cascadeDelay: PropTypes.number,
  // Observer props
  threshold: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.arrayOf(PropTypes.number),
  ]),
  root: PropTypes.object,
  rootMargin: PropTypes.string,
  triggerOnce: PropTypes.bool,
  // Callback props
  onEnter: PropTypes.func,
  onLeave: PropTypes.func,
  onChange: PropTypes.func,
  // Component props
  as: PropTypes.elementType,
  className: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.func,
  ]),
};

export { InView };
export default InView;
